<template>
  <div
    class="customCard adm-card"
    :class="[`adm-card--${deadlineCategory}`, { 'adm-card--selected': selected }]"
    role="button"
    tabindex="0"
    @click="$emit('select')"
    @keydown.enter="$emit('select')"
  >
    <div class="customCard__left">
      <swiper
        class="customCard__left__swiper"
        :slides-per-view="1"
        :speed="1000"
        :autoplay="true"
        :pagination="{ clickable: true }"
        :lazy="true"
        :loop="images.length > 1"
      >
        <swiper-slide
          v-for="(img, idx) in images"
          :key="idx"
          class="customCard__left__swiper__slide"
        >
          <img :src="img.image" :alt="programName" @error="onImgError" />
        </swiper-slide>
      </swiper>

      <div v-if="priority" class="adm-card__priority">#{{ priority }}</div>

      <div v-if="program?.is_student_choose" class="bilim">
        <img src="@/assets/icons/bilim.svg" alt="" />
        <p>ВЫБОР СТУДЕНТА</p>
      </div>
    </div>

    <div class="customCard__right">
      <div class="customCard__right__top">
        <p class="customCard__right__top__name">{{ university }}</p>
        <p class="customCard__right__top__spec">{{ programName }}</p>
      </div>

      <div class="line"></div>

      <div class="customCard__right__rate">
        <p v-if="program?.top">{{ program.top }}</p>
        <span v-if="category">{{ category }}</span>
      </div>

      <div class="adm-card__meta">
        <span class="adm-card__status" :class="statusBadgeClass">{{ statusLabel }}</span>
        <span v-if="packageTitle" class="adm-card__package">{{ packageTitle }}</span>
      </div>

      <div class="adm-deadline" :class="`adm-deadline--${deadlineCategory}`">
        <div class="adm-deadline__row">
          <span class="adm-deadline__label">Крайний срок</span>
          <span v-if="daysUntilDeadline !== null" class="adm-deadline__count">
            {{ daysCounterBadge }}
          </span>
        </div>
        <p class="adm-deadline__date">{{ formattedDeadline }}</p>
        <p v-if="daysUntilHint" class="adm-deadline__hint">{{ daysUntilHint }}</p>
      </div>

      <div class="customCard__right__buttons">
        <button type="button" class="customCard__right__buttons__red" @click.stop="goProgram">
          Подробнее
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { Swiper, SwiperSlide } from 'swiper/vue';
import 'swiper/css';
import 'swiper/css/pagination';
import SwiperCore, { A11y, Autoplay, Pagination } from 'swiper';
import 'swiper/swiper.min.css';
import noPhoto from '@/assets/no-photo.png';
import {
  daysUntilAdmissionDeadline,
  formatAdmissionDeadline,
} from '@/utils/admissionDeadline';

SwiperCore.use([Pagination, A11y, Autoplay]);

export default {
  name: 'AdmissionCard',
  components: { Swiper, SwiperSlide },
  props: {
    admission: { type: Object, required: true },
    selected: { type: Boolean, default: false },
  },
  emits: ['select'],
  computed: {
    program() {
      return this.admission?.program || {};
    },
    programName() {
      return this.program.programName || this.program.name || '—';
    },
    university() {
      return this.program.university?.title || this.program.university || '—';
    },
    category() {
      return this.program.subcategory?.category?.title || this.program.category || '';
    },
    priority() {
      return this.admission?.admission_status?.priority || null;
    },
    packageTitle() {
      const pkg = this.admission?.admission_status?.package;
      if (!pkg?.id || pkg.id === 6) return '';
      return pkg.title || '';
    },
    images() {
      const imgs = this.program.images;
      if (imgs?.length) return imgs;
      return [{ image: noPhoto }];
    },
    deadlineValue() {
      return (
        this.program.autumn_admission_end_date
        || this.admission?.admission_status?.second_date
        || this.admission?.admission_status?.first_date
        || null
      );
    },
    daysUntilDeadline() {
      return daysUntilAdmissionDeadline(this.deadlineValue);
    },
    deadlineCategory() {
      const days = this.daysUntilDeadline;
      if (days === null) return 'none';
      if (days < 0) return 'overdue';
      if (days <= 7) return 'critical';
      if (days <= 30) return 'urgent';
      return 'safe';
    },
    formattedDeadline() {
      return formatAdmissionDeadline(this.deadlineValue);
    },
    daysCounterBadge() {
      const days = this.daysUntilDeadline;
      if (days === null) return '';
      if (days < 0) return `−${Math.abs(days)} дн.`;
      if (days === 0) return 'Сегодня';
      return `${days} дн.`;
    },
    daysUntilHint() {
      const days = this.daysUntilDeadline;
      if (days === null) return '';
      if (days < 0) return `Просрочено на ${Math.abs(days)} ${this.daysWord(Math.abs(days))}`;
      if (days === 0) return 'Сегодня последний день';
      if (days === 1) return 'Остался 1 день до дедлайна';
      if (days <= 30) return `Осталось ${days} ${this.daysWord(days)} до дедлайна`;
      return '';
    },
    statusLabel() {
      const s = this.admission?.admission_status?.status;
      if (s === 0) return 'Принято';
      if (s === 1) return 'Ожидание ответа';
      if (s === 2) return 'Отклонено';
      return 'В работе';
    },
    statusBadgeClass() {
      const s = this.admission?.admission_status?.status;
      if (s === 0) return 'adm-card__status--success';
      if (s === 1) return 'adm-card__status--pending';
      if (s === 2) return 'adm-card__status--rejected';
      return 'adm-card__status--muted';
    },
  },
  methods: {
    daysWord(n) {
      const abs = Math.abs(n) % 100;
      const n1 = abs % 10;
      if (abs >= 11 && abs <= 19) return 'дней';
      if (n1 === 1) return 'день';
      if (n1 >= 2 && n1 <= 4) return 'дня';
      return 'дней';
    },
    goProgram() {
      const id = this.program.id;
      if (id) this.$router.push({ name: 'program', params: { id } });
    },
    onImgError(e) {
      e.target.src = noPhoto;
    },
  },
};
</script>

<style lang="scss" scoped>
.customCard {
  width: 100%;
  display: flex;
  align-items: stretch;
  justify-content: space-between;
  gap: 12px;
  background: #fff;
  border: 1px solid #e8eaed;
  border-radius: 10px;
  padding: 12px;
  cursor: pointer;
  transition: border-color 0.2s, box-shadow 0.2s, transform 0.2s;

  &.adm-card--selected {
    border-color: #e8192c;
    background: #fffafa;
    box-shadow: 0 0 0 3px rgba(232, 25, 44, 0.18), 0 8px 24px rgba(232, 25, 44, 0.12);
    transform: scale(1.03);
    z-index: 2;
    position: relative;
  }

  &:hover:not(.adm-card--selected) {
    border-color: #c5d0d8;
    box-shadow: 0 4px 14px rgba(17, 65, 93, 0.08);
  }

  &:focus-visible {
    outline: 2px solid #e8192c;
    outline-offset: 2px;
  }

  @media (min-width: 640px) {
    gap: 0;
  }

  &__left {
    width: 30%;
    position: relative;
    height: 100%;

    &__swiper {
      height: 100%;
      width: 100%;

      &__slide {
        height: 100%;
        width: 100%;

        img {
          width: 100%;
          height: 100%;
          aspect-ratio: 1;
          border-radius: 10px;
          object-fit: cover;
        }
      }
    }

    .bilim {
      position: absolute;
      z-index: 998;
      top: 10px;
      left: 50%;
      width: max-content;
      transform: translate(-50%, 0);
      display: flex;
      gap: 5px;
      color: white;
      padding: 5px;
      border-radius: 5px;
      background-color: #fd2d2a;
      align-items: center;

      img {
        width: 10px;
      }

      p {
        font-size: min(max(6px, calc(0.375rem + ((1vw - 7.68px) * 0.5208))), 12px);
        margin: 0;
      }
    }
  }

  &__right {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding-left: 12px;
    min-width: 0;

    @media (min-width: 640px) {
      width: 68%;
      padding-left: 20px;
    }

    &__top {
      display: flex;
      flex-direction: column;
      gap: 5px;

      &__name {
        color: #0000008a;
        margin: 0;
        font-size: 13px;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }

      &__spec {
        font-size: min(max(14px, calc(0.875rem + ((1vw - 7.68px) * 0.5208))), 20px);
        font-weight: 700;
        margin: 0;
        cursor: pointer;
        line-height: 1.3;
        display: -webkit-box;
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;
        overflow: hidden;

        &:hover {
          color: #11415d;
        }
      }
    }

    &__rate {
      font-weight: 700;
      color: #fd2d2a;
      display: flex;
      gap: 10px;
      align-items: center;
      flex-wrap: wrap;
      font-size: 13px;

      p {
        margin: 0;
      }

      span {
        color: #11415d !important;
      }
    }

    &__buttons {
      display: flex;
      gap: 10px;
      align-items: stretch;
      margin-top: auto;

      button {
        padding: 7px 12px;
        border-radius: 5px;
        font-size: min(max(10px, calc(0.625rem + ((1vw - 7.68px) * 0.3472))), 14px);
        justify-content: center;
        display: flex;
        align-items: center;
        gap: 5px;
        cursor: pointer;
        border: none;
        font-family: inherit;
      }

      &__red {
        background-color: #fd2d2a;
        color: white;
      }
    }
  }

  .line {
    border-top: 1px solid #0000001a;
    width: 100%;
  }

  @media (max-width: 768px) {
    margin-bottom: 0;
    padding: 10px;

    .line {
      display: none;
    }

    &__left {
      width: 32%;

      .bilim {
        padding: 2px;
      }
    }

    &__right {
      width: 66%;
      gap: 6px;
      padding-left: 10px;

      &__top__spec {
        font-size: 16px !important;
      }

      &__top__name {
        font-size: 14px !important;
      }

      &__buttons__red {
        width: fit-content;
        padding: 6px 10px !important;
        font-size: 14px !important;
      }
    }
  }
}

.adm-card__priority {
  position: absolute;
  top: 8px;
  left: 8px;
  z-index: 999;
  padding: 3px 8px;
  border-radius: 6px;
  background: rgba(17, 65, 93, 0.92);
  color: #fff;
  font-size: 11px;
  font-weight: 700;
}

.adm-card__meta {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
}

.adm-card__status {
  padding: 3px 10px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 700;
  line-height: 1.4;

  &--success { background: #e8faf0; color: #0d9b3d; }
  &--pending { background: #fff4e5; color: #c76a00; }
  &--rejected { background: #fde8ea; color: #e8192c; }
  &--muted { background: #f3f4f6; color: #6b7280; }
}

.adm-card__package {
  font-size: 11px;
  font-weight: 600;
  color: #11415d;
}

.adm-deadline {
  padding: 10px 12px;
  border-radius: 8px;
  border: 1px solid #e8eaed;
  background: #fafbfc;

  &--none,
  &--safe {
    .adm-deadline__date { color: #11415d; }
    .adm-deadline__count { background: #e8f0f5; color: #11415d; }
    .adm-deadline__hint { color: #6b7280; }
  }

  &--urgent {
    background: #fffbf5;
    border-color: #ffe0a3;

    .adm-deadline__date { color: #b35c00; }
    .adm-deadline__count { background: #ffe7b3; color: #b35c00; }
    .adm-deadline__hint { color: #c76a00; }
  }

  &--critical,
  &--overdue {
    background: #fff8f8;
    border-color: #ffc9ce;

    .adm-deadline__date { color: #c9000e; }
    .adm-deadline__count { background: #e8192c; color: #fff; }
    .adm-deadline__hint { color: #b50011; }
  }
}

.adm-deadline__row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 6px;
  margin-bottom: 2px;
}

.adm-deadline__label {
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: #9ca3af;
}

.adm-deadline__count {
  flex-shrink: 0;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 10px;
  font-weight: 700;
}

.adm-deadline__date {
  margin: 0;
  font-size: 15px;
  font-weight: 800;
  line-height: 1.25;
}

.adm-deadline__hint {
  margin: 4px 0 0;
  font-size: 11px;
  font-weight: 600;
  line-height: 1.35;
}
</style>
